import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router'
import { DesktopOnly } from '@/components/DesktopOnly'
import { ErrorCard } from '@/components/ErrorCard'
import { ConfirmDialog } from '@/components/patterns/ConfirmDialog'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { PageHeader } from '@/features/preinvest/components/PageHeader'
import { toast } from '@/lib/toast'
import { downloadImportReport, getImport, undoImport } from './api'
import { importedRecordsPath } from './links'
import { OUTCOME_LABELS, OUTCOME_ORDER } from './rowChoices'
import type { ImportBatch, UndoResult } from './types'

const UNDO_WINDOW_MS = 24 * 60 * 60 * 1000

function formatDateTime(value: string | number): string {
  return new Date(value).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' })
}

function count(amount: number, one: string, many: string): string {
  return `${amount} ${amount === 1 ? one : many}`
}

/** What the undo did, and what stayed because someone worked on it since. */
function undoMessage(result: UndoResult): string {
  const head = result.removed > 0 ? `Import ongedaan gemaakt: ${count(result.removed, 'record', 'records')} verwijderd.` : 'Import ongedaan gemaakt.'
  if (result.kept === 0) return head
  const shown = result.kept_names.slice(0, 3)
  const names = `${shown.join(', ')}${result.kept_names.length > shown.length ? ', …' : ''}`
  const kept = result.kept === 1 ? '1 record bleef staan' : `${result.kept} records bleven staan`
  return `${head} ${kept} omdat er al iets mee gedaan is (${names}).`
}

/** ④ Klaar (spec-63 E6): what came of each row, the report, "Bekijk geïmporteerde …" and undo. */
export function ImportResultPage() {
  const { id = '' } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const handed = (location.state as { batch?: ImportBatch } | null)?.batch
  const [batch, setBatch] = useState<ImportBatch | null>(handed?.id === id && handed.results ? handed : null)
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)
  const [confirming, setConfirming] = useState(false)
  const [undoing, setUndoing] = useState(false)
  const mounted = useRef(true)
  const loadedId = batch?.id

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    if (loadedId === id) return
    let cancelled = false
    getImport(id)
      .then((next) => {
        if (cancelled) return
        setBatch(next)
        setError(null)
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(caught instanceof Error ? caught.message : 'Import laden mislukt.')
      })
    return () => {
      cancelled = true
    }
  }, [id, loadedId, attempt])

  async function undo() {
    setUndoing(true)
    try {
      const result = await undoImport(id)
      setBatch(result.batch)
      // Back (from the list of imported records) and a reload show this page from the history
      // entry, so it must hold the import as it is now. Not once the user has left the page.
      if (mounted.current) {
        navigate(`${location.pathname}${location.search}`, { replace: true, state: { batch: result.batch } })
      }
      setConfirming(false)
      toast.success(undoMessage(result))
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'Ongedaan maken mislukt.')
    } finally {
      setUndoing(false)
    }
  }

  if (error) {
    return (
      <ErrorCard
        title={error}
        onRetry={() => {
          setError(null)
          setAttempt((current) => current + 1)
        }}
      />
    )
  }
  if (!batch || batch.id !== id) return <Skeleton className="h-40 w-full" />

  const contacts = batch.mode === 'contacten'
  const until = formatDateTime(new Date(batch.created_at).getTime() + UNDO_WINDOW_MS)

  return (
    <DesktopOnly>
      <section className="flex flex-col gap-4">
        <PageHeader
          title={`Import ${batch.filename}`}
          subtitle={`${formatDateTime(batch.created_at)} door ${batch.created_by_name ?? 'onbekend'} · ${contacts ? 'Contacten' : 'Organisaties'}`}
          actions={
            <>
              <Button variant="outline" className="bg-white" onClick={() => navigate(importedRecordsPath(batch))}>
                {contacts ? 'Bekijk geïmporteerde contacten' : 'Bekijk geïmporteerde organisaties'}
              </Button>
              <Button
                variant="outline"
                className="bg-white"
                onClick={() =>
                  void downloadImportReport(batch.id).catch(() => toast.error('Importrapport downloaden mislukt.'))
                }
              >
                Download importrapport
              </Button>
              {batch.can_undo ? (
                <Button variant="destructive" onClick={() => setConfirming(true)}>
                  Import ongedaan maken
                </Button>
              ) : null}
            </>
          }
        />
        {batch.undone_at ? (
          <p role="status" className="vonk-card p-3 text-sm text-warning">
            Ongedaan gemaakt op {formatDateTime(batch.undone_at)}
            {batch.undone_by_name ? ` door ${batch.undone_by_name}` : ''}.
          </p>
        ) : batch.can_undo ? (
          <p className="text-sm text-muted-foreground">Ongedaan maken kan tot {until}.</p>
        ) : null}
        <dl className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          {OUTCOME_ORDER.map((outcome) => (
            <div key={outcome} className="vonk-card p-3">
              <dt className="text-xs font-semibold text-muted-foreground">{OUTCOME_LABELS[outcome]}</dt>
              <dd className="text-2xl font-semibold text-foreground tabular-nums">{batch.counts[outcome] ?? 0}</dd>
            </div>
          ))}
        </dl>
        {contacts && (batch.counts.organisaties_aangemaakt ?? 0) > 0 ? (
          <p className="text-sm text-muted-foreground">
            {count(batch.counts.organisaties_aangemaakt ?? 0, 'nieuwe organisatie', 'nieuwe organisaties')} aangemaakt.
          </p>
        ) : null}
        <section className="vonk-card flex flex-col gap-2 p-4" aria-labelledby="import-rows">
          <h2 id="import-rows" className="text-base font-semibold text-foreground">
            Per rij
          </h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold text-muted-foreground">
                <th scope="col" className="py-2 pr-3">
                  Rij
                </th>
                <th scope="col" className="py-2 pr-3">
                  Naam
                </th>
                <th scope="col" className="py-2 pr-3">
                  Organisatie
                </th>
                <th scope="col" className="py-2 pr-3">
                  Uitkomst
                </th>
                <th scope="col" className="py-2">
                  Toelichting
                </th>
              </tr>
            </thead>
            <tbody>
              {batch.results.map((result) => (
                <tr key={result.row_number} className="border-t border-border">
                  <td className="py-2 pr-3 tabular-nums">{result.row_number}</td>
                  <td className="py-2 pr-3">
                    {result.person_id && result.person_name ? (
                      <Link
                        to={`/relaties/contacten/${result.person_id}`}
                        className="font-semibold text-primary underline underline-offset-2"
                      >
                        {result.person_name}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    {result.organisation_id && result.organisation_name ? (
                      <Link
                        to={`/relaties/organisaties/${result.organisation_id}`}
                        className="font-semibold text-primary underline underline-offset-2"
                      >
                        {result.organisation_name}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="py-2 pr-3">{OUTCOME_LABELS[result.outcome]}</td>
                  <td className="py-2 text-muted-foreground">{result.message ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <ConfirmDialog
          open={confirming}
          onOpenChange={setConfirming}
          title="Import ongedaan maken?"
          description={`Wat ${batch.filename} aanmaakte (${count(batch.counts.created ?? 0, 'record', 'records')}) wordt verwijderd, behalve wat het team er sindsdien mee deed. Aanvullingen van bestaande ${contacts ? 'contacten' : 'organisaties'} worden niet teruggedraaid.`}
          confirmLabel="Ongedaan maken"
          busy={undoing}
          onConfirm={() => void undo()}
        />
      </section>
    </DesktopOnly>
  )
}
